import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../i18n'
import { useClientOutdated } from '../lib/client-floor'
import { isDesktopApp, useUpdateReady } from '../lib/desktop'

/**
 * The blocking "this client is below the backend's version floor" state (lib/client-floor.ts).
 *
 * Absent while the floors are unset or met. Once shown it never goes away in place: the running
 * bundle cannot become newer without a reload, and every room connection is already closed. So it
 * is a modal `alertdialog` with ONE way out — Reload in a browser, Restart in Niobe Desktop (the same
 * restart the update banner uses, which applies whatever update the shell has staged; the floor
 * already asked the shell to check both lanes). Escape does nothing and focus stays on the action:
 * there is nothing behind it the member may keep using. Rendered once at the app root, beside the
 * update banner, so it covers every view.
 */
export function ClientOutdatedDialog() {
  const { t } = useLang()
  const outdated = useClientOutdated()
  const { restarting, restart } = useUpdateReady()
  const actionRef = useRef<HTMLButtonElement>(null)
  const show = outdated !== null

  useEffect(() => {
    if (show) actionRef.current?.focus()
  }, [show])

  if (!show) return null
  const desktop = isDesktopApp()

  return createPortal(
    <div className="modal-backdrop outdated-backdrop">
      <div
        className="modal confirm-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="client-outdated-title"
        aria-describedby="client-outdated-msg"
        onKeyDown={(e) => {
          // A single focusable control: keep focus on it rather than let Tab escape into the page
          // this dialog blocks.
          if (e.key === 'Tab') {
            e.preventDefault()
            actionRef.current?.focus()
          }
        }}
      >
        <div className="modal-head">
          <h2 id="client-outdated-title">{desktop ? t('outdated_desktop_title') : t('outdated_web_title')}</h2>
        </div>
        <div className="modal-body prompt-body">
          <p id="client-outdated-msg" className="confirm-msg">
            {desktop ? t('outdated_desktop_msg') : t('outdated_web_msg')}
          </p>
          <div className="prompt-acts">
            {desktop ? (
              <button ref={actionRef} type="button" className="mini ok" disabled={restarting} onClick={restart}>
                {restarting ? t('update_restarting') : t('outdated_restart')}
              </button>
            ) : (
              <button ref={actionRef} type="button" className="mini ok" onClick={() => window.location.reload()}>
                {t('outdated_reload')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
